/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "htmlFor" }] */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Checkbox, Input, Modal, Spin,
} from 'antd';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';
import Button from '../../../../components/Button';
import { captureAnalytics } from '../../../../actions/preload';
import { formatValidFaxNumber } from '../../../../global/validation';
import ContactPicker from './ContactPicker';
import useSenderContactForm, { normalizedFax } from './useSenderContactForm';

const SenderContactEditor = ({
  record, onClose, onSave, onSaved, surface,
}) => {
  const details = record.sender_contact_details;
  const {
    form, update, selectContact, save, numberChanged,
  } = useSenderContactForm({
    record, onSave, onSaved, surface,
  });
  const {
    contact, office, person, fax, candidates, picking, loading, loadFailed, attempt, saving, error, confirmed,
  } = form;
  const officeInput = useRef(null);
  useEffect(() => {
    if (!loading && !picking) officeInput.current?.focus();
  }, [loading, picking]);
  const close = () => {
    if (saving) return;
    captureAnalytics('sender_contact_editor_closed', { surface });
    onClose();
  };

  return (
    <Modal visible centered width={460} className="sender-contact-modal" title={contact ? 'Edit contact' : 'Save sender as contact'} onCancel={close} maskClosable={false} keyboard={!saving} closable={!saving} footer={null}>
      <div className="sender-contact-source">
        <span>Received from</span>
        <strong>{formatValidFaxNumber(record.sender_email)}</strong>
      </div>
      <Spin spinning={loading}>
        {error && <Alert type="error" showIcon message={error} />}
        {loadFailed && <button type="button" className="sender-contact-link" onClick={() => { captureAnalytics('sender_contact_lookup_retried', { surface }); update({ attempt: attempt + 1 }); }}>Retry contact lookup</button>}
        {picking ? (
          <>
            {candidates.length > 1 && <p className="sender-contact-hint">Several contacts use this fax number. Choose the one to update.</p>}
            <ContactPicker initialContacts={candidates} onSelect={(selected) => { captureAnalytics('sender_contact_existing_selected', { surface }); selectContact(selected); }} />
            <button type="button" className="sender-contact-link" onClick={() => { captureAnalytics('sender_contact_create_selected', { surface }); selectContact(null); }}>Back to new contact</button>
          </>
        ) : (
          <form onSubmit={(event) => { event.preventDefault(); save(); }}>
            {!contact && <button type="button" className="sender-contact-link sender-contact-existing" disabled={loading} onClick={() => { captureAnalytics('sender_contact_search_opened', { surface }); update({ picking: true }); }}>Update an existing contact</button>}
            <label htmlFor="sender-contact-office">Office / organization name</label>
            <Input maxLength={255} id="sender-contact-office" ref={officeInput} value={office} disabled={saving || loading} onChange={(event) => update({ office: event.target.value })} placeholder="e.g. Desert Imaging" autoComplete="off" />
            <label htmlFor="sender-contact-fax">Fax number</label>
            <Input id="sender-contact-fax" value={fax} disabled={saving || loading} onChange={(event) => { update({ fax: event.target.value, confirmed: false }); }} inputMode="tel" />
            {contact && normalizedFax(fax) !== normalizedFax(record.sender_email) && <button type="button" className="sender-contact-link" disabled={saving} onClick={() => { captureAnalytics('sender_contact_incoming_number_selected', { surface }); update({ fax: formatValidFaxNumber(record.sender_email), confirmed: false }); }}>Use incoming fax number</button>}
            {numberChanged && (
              <div className="sender-contact-number-change">
                <p>{`Replace ${formatValidFaxNumber(contact.contact)} with ${fax}? Previously received faxes keep their original source number.`}</p>
                <Checkbox checked={confirmed} disabled={saving} onChange={(event) => { update({ confirmed: event.target.checked }); captureAnalytics('sender_contact_number_change_confirmed', { surface }); }}>Change the saved fax number</Checkbox>
              </div>
            )}
            <details className="sender-contact-more" open={person ? true : undefined} onToggle={(event) => { if (event.currentTarget.open) captureAnalytics('sender_contact_details_expanded', { surface }); }}>
              <summary>More details</summary>
              <label htmlFor="sender-contact-person">Contact person (optional)</label>
              <Input maxLength={255} id="sender-contact-person" value={person} disabled={saving || loading} onChange={(event) => update({ person: event.target.value })} autoComplete="off" />
            </details>
            <div className="sender-contact-scope">
              {details.personal ? <UserOutlined /> : <TeamOutlined />}
              <span>{details.personal ? 'Only visible to you' : 'Shared with your organization'}</span>
            </div>
            <div className="sender-contact-footer">
              <Button type="secondary" size="small" label="Cancel" disabled={saving} onClick={close} />
              <Button size="small" htmlType="submit" label={contact ? 'Save changes' : 'Save contact'} loading={saving} disabled={loading || loadFailed} />
            </div>
          </form>
        )}
      </Spin>
    </Modal>
  );
};

SenderContactEditor.propTypes = {
  record: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired, onSave: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired, surface: PropTypes.string.isRequired,
};
export default SenderContactEditor;
